import { useEffect, useRef, useState } from 'react';
import './index.css';

const demoCases = [
  { name: 'HYTE X50', image: '/assets/custom-pc/hyte-x50.webp', url: '/product/custom-pc/hyte-x50-custom-gaming-pc/' },
  { name: 'ASUS Prime AP202', image: '/assets/custom-pc/prime-ap202.webp', url: '/product/custom-pc/asus-prime-ap202-custom-gaming-pc/' },
  { name: 'TD500 Mesh White', image: '/assets/custom-pc/td500-white.webp', url: '/product/custom-pc/masterbox-td500-mesh-white-custom-gaming-pc/' },
];
export function Arrow({ direction = 'right' }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d={direction === 'left' ? 'M15 5 8 12l7 7M8 12h13' : 'm9 5 7 7-7 7M3 12h13'} /></svg>;
}
export default function CustomPCHero({ cases = demoCases }) {
  const [active, setActive] = useState(cases.length > 1 ? 1 : 0);
  const [playing, setPlaying] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(true);
  const [reduced, setReduced] = useState(false);
  const [dragging, setDragging] = useState(false);
  const section = useRef(null), drag = useRef(null), locked = useRef(0);
  const selected = cases[active];
  useEffect(() => {
    const media = matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => { setReduced(media.matches); if (media.matches) setPlaying(false); };
    setReduced(media.matches); setPlaying(!media.matches);
    media.addEventListener('change', sync);
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: .25 });
    observer.observe(section.current);
    const hidden = () => { if (document.hidden) setVisible(false); else setVisible(section.current.getBoundingClientRect().bottom > 0 && section.current.getBoundingClientRect().top < innerHeight); };
    document.addEventListener('visibilitychange', hidden);
    return () => { observer.disconnect(); media.removeEventListener('change', sync); document.removeEventListener('visibilitychange', hidden); };
  }, []);
  useEffect(() => {
    if (!playing || hovered || focused || !visible || reduced || cases.length < 2) return;
    const timer = setInterval(() => setActive(index => (index + 1) % cases.length), 6000);
    return () => clearInterval(timer);
  }, [playing, hovered, focused, visible, reduced, cases.length]);
  function move(direction) {
    setPlaying(false);
    if (performance.now() < locked.current) return;
    locked.current = performance.now() + (reduced ? 0 : 600);
    setActive(index => (index + direction + cases.length) % cases.length);
  }
  function startDrag(event) {
    if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0) || cases.length < 2) return;
    setPlaying(false);
    drag.current = { id: event.pointerId, startX: event.clientX, startY: event.clientY, lastX: event.clientX, distance: 0, horizontal: false, step: Math.max(48, Math.min(90, event.currentTarget.clientWidth * .16)) };
    event.currentTarget.setPointerCapture(event.pointerId);
  }
  function continueDrag(event) {
    const gesture = drag.current;
    if (!gesture || gesture.id !== event.pointerId) return;
    const dx = event.clientX - gesture.startX, dy = event.clientY - gesture.startY;
    if (!gesture.horizontal) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
      if (Math.abs(dy) > Math.abs(dx)) { endDrag(event); return; }
      gesture.horizontal = true;
      setDragging(true);
    }
    gesture.distance += gesture.lastX - event.clientX;
    gesture.lastX = event.clientX;
    const steps = Math.trunc(gesture.distance / gesture.step);
    if (steps) {
      gesture.distance -= steps * gesture.step;
      setActive(index => (index + steps % cases.length + cases.length) % cases.length);
    }
  }
  function endDrag(event) {
    if (!drag.current || drag.current.id !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }
  if (!selected) return null;
  return <section ref={section} className="cph-hero" aria-label="Custom PC builder" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onFocusCapture={() => setFocused(true)} onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
    <div className="cph-ambient" aria-hidden="true" />
    <div className="cph-inner">
      <div className="cph-copy">
        <p className="cph-eyebrow">CUSTOM GAMING PCS</p>
        <h1 className="cph-title">Pick your case.<br /><span>Build your gaming PC.</span></h1>
        <a className="cph-secondary" href="/ai-workstations/">Looking for an AI workstation? <span aria-hidden="true">→</span></a>
        <p className="cph-description">Choose your components. Customize your performance.<br /> Create a custom gaming PC built around you.</p>
      </div>
      <div className="cph-showroom" role="region" aria-roledescription="carousel" aria-label="Choose a PC case">
        <div className={`cph-stage${dragging ? ' cph-stage-dragging' : ''}`} onPointerDown={startDrag} onPointerMove={continueDrag} onPointerUp={endDrag} onPointerCancel={endDrag} onLostPointerCapture={endDrag}>
          <div className="cph-orbit" aria-hidden="true" />
          {cases.map((pc, index) => {
            const position = index === active ? 'front' : index === (active + 1) % cases.length ? 'right' : index === (active - 1 + cases.length) % cases.length ? 'left' : 'back';
            return <div key={pc.url} className={`cph-machine cph-machine-${position}`} aria-hidden="true"><img src={pc.image} alt="" width="640" height="720" loading={position === 'back' ? 'lazy' : 'eager'} fetchPriority={index === 1 ? 'high' : 'auto'} draggable="false" /></div>;
          })}
        </div>
        <div className="cph-controls">
          <button type="button" className="cph-arrow" aria-label="Previous case" onClick={() => move(-1)} disabled={cases.length < 2}><Arrow direction="left" /></button>
          <a className="cph-option cph-option-active" href={selected.url}><span className="cph-configure-label">Configure</span><span className="cph-case-name">{selected.name}</span></a>
          <button type="button" className="cph-arrow" aria-label="Next case" onClick={() => move(1)} disabled={cases.length < 2}><Arrow /></button>
        </div>
        <div className="cph-playback"><span aria-live={playing ? 'off' : 'polite'}>{active + 1} / {cases.length}</span><button type="button" className="cph-pause" aria-label={playing ? 'Pause automatic rotation' : 'Start automatic rotation'} onClick={() => setPlaying(value => !value)} disabled={reduced || cases.length < 2}>{playing ? 'Pause' : 'Play'}<span aria-hidden="true">{playing ? 'Ⅱ' : '▷'}</span></button></div>
      </div>
      <div className="cph-actions">
        <a className="cph-primary" href="#choose-your-case">Explore all cases <Arrow /></a>
      </div>
    </div>
  </section>;
}
